<template>
  <v-container class="pa-0 fill-height" fluid>

    <v-fade-transition hide-on-leave>
      <template v-if="loading">
        <v-row align="center" class="fill-height my-12" justify="center">
          <v-progress-circular size="64" indeterminate color="primary"/>
        </v-row>
      </template>
    </v-fade-transition>

    <template v-if="!loading">
      <v-row justify="center" no-gutters class="fill-height">
        <v-slide-y-transition hide-on-leave>
          <v-col v-if="messages.length > 0" cols="12" style="padding-bottom: 74px;">
            <div :key="disclaimerMessage.index">
              <chat-message-item :message="disclaimerMessage"/>
            </div>
            <template v-for="(message, index) in formattedMessages">
              <div :key="message.index">
                <chat-message-item :message="message" @vote-down="vote" @vote-up="vote"
                                   :loading="message.inflight || false"/>
                <v-divider v-if="index < formattedMessages.length - 1" class="my-0 py-0 rowDivider"/>
              </div>
            </template>
          </v-col>
        </v-slide-y-transition>
        <v-fade-transition hide-on-leave>
          <v-col v-if="messages.length === 0" class="fill-height" cols="12" style="padding-bottom: 74px;">
            <v-row align="center" class="fill-height" justify="center" no-gutters>
              <v-col class="my-7" cols="10">
                <div class="text-center mb-5">
                  <img src="/img/Harvia_sauna_spa-logo.svg" height="200px" width="200px" alt="Harvia" />
                </div>
                <div class="text-h6 text-sm-h5 text-md-center">
                  {{ $t('sales_ask_anything') }}
                </div>
                <div class="text-body-1 text-md-center mt-3">
                  {{ $t('sales_ai_disclaimer') }}
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-fade-transition>

        <support-ticket-dialog ref="supportTicketDialog" :api-client="salesApi"/>

        <div id="chat-end"/>

      </v-row>

      <v-bottom-sheet :value="true" hide-overlay no-click-animation persistent :retain-focus="false">
        <v-container
          class="rowQuestion chat-input-holder" fluid>
          <v-row justify="center">
            <v-col cols="12" lg="10" md="10" xl="8">
              <v-row>
                <v-col cols="12" md="10" offset-md="1">
                  <chat-input :loading="sendingMessage"
                              :temperature="temperature"
                              @submit="sendMessage"
                              @temperature="setTemperature"
                              @start-new-chat="startNewChat"/>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-container>
      </v-bottom-sheet>
    </template>

  </v-container>
</template>

<script>
import ChatMessageItem from "@/views/EmbedChat/components/ChatMessageItem";
import ChatInput from "@/views/EmbedChat/components/ChatInput";
import salesApi from "@/api/sales-api";
import axios from "axios";
import store from "@/store";
import {useSessionStorage} from "@vueuse/core";
import {v4 as uuidv4} from 'uuid';
import SupportTicketDialog from "@/views/EmbedChat/components/SupportTicketDialog.vue";
import {fetchEventSource} from '@microsoft/fetch-event-source';
import {getActiveRegionKey} from "@/config/regions";

let chatStorage;

export default {
  name: "SalesView",
  components: {SupportTicketDialog, ChatInput, ChatMessageItem},
  data() {
    return {
      messages: [],
      temperature: 20,
      loading: true,
      subtleLoading: true,
      sendingMessage: false,
      activeChat: null,
      chatId: null,
      user: null,
      salesApi,
    };
  },
  computed: {
    disclaimerMessage() {
      return {
        id: 'disclaimer_message',
        index: -1,
        message: this.$t('sales_ai_disclaimer'),
        type: 'reply'
      }
    },
    formattedMessages() {
      let index = 0;
      const messages = [...this.messages];
      return messages.flatMap(m => ([
        {
          ...m,
          id: `${m.id}-q-${uuidv4()}`,
          originalId: m.id,
          chatId: m.chat_id,
          type: 'question',
          message: m.message,
          index: index++,
          selectedIndex: m.index,
          sourceMetadata: m.source,
          sourceUrl: m.sourceUrl,
          lastUpdatedTimestamp: m.lastUpdatedTimestamp || m.timestamp,
          temperature: m.temperature
        },
        {
          ...m,
          id: `${m.id}-a-${uuidv4()}`,
          originalId: m.id,
          chatId: m.chat_id,
          type: 'reply',
          message: m.reply,
          index: index++,
          selectedIndex: m.index,
          sourceMetadata: m.source,
          sourceUrl: m.sourceUrl,
          temperature: m.temperature,
          lastUpdatedTimestamp: m.lastUpdatedTimestamp || m.timestamp,
          inflight: m.inflight,
        }]))
    }
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('fi-FI', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    },
    viewChat(chat) {
      this.chatId = chat.id;
      this.activeChat = chat.timestamp;
      this.reloadMessages();
    },
    async reloadMessages() {
      if (this.loading === false) {
        this.subtleLoading = true;
      } else {
        this.loading = true;
      }
      try {
        const messages = await salesApi.getChatMessages(this.chatId);
        messages.sort((a, b) => a.timestamp - b.timestamp);
        this.messages = messages || [];
        this.scrollToEnd();
      } catch (error) {
        alert(error);
      }
      this.loading = false;
      this.subtleLoading = false;
    },
    async sendMessage(messageText) {
      this.sendingMessage = true;
      if (this.chatId.startsWith('dummy')) {
        await this.createNewChat(messageText);
      }

      this.$sendAnalyticsEvent('chat-message-sent');

      const uuid = uuidv4();
      const placeholderChatMessage = {
        id: uuid,
        chat_id: this.chatId,
        message: messageText,
        timestamp: new Date(),
        error: false,
        temperature: this.temperature / 100,
        lastUpdatedTimestamp: new Date().getTime(),
        indexName: this.$i18n.locale,
        sourceUrl: '',
        reply: '',
        inflight: true,
        autoScrollEnabled: true,
      };

      try {
        this.messages.push(placeholderChatMessage);
        this.scrollToEnd();
        const placeholderChatMessageIndex = this.messages.findIndex(m => m.id === uuid)

        const ctrl = new AbortController();
        let chatMessage = [];
        try {
          await fetchEventSource(`/api/v2/sales/chat/${this.chatId}/message-stream`, {
            method: 'POST',
            body: JSON.stringify({
              message: messageText,
              locale: getActiveRegionKey(),
            }),
            headers: {
              'accept-encoding': '',
              'Content-Type': 'application/json',
              'x-api-key': axios.defaults.headers.common['x-api-key'],
              'Accept': 'text/event-stream'
            },
            signal: ctrl.signal,
            openWhenHidden: true,
            onmessage: (ev) => {
              const msg = ev.data.replaceAll('___|n|___', '\n')
              if (ev.event === 'id') {
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  id: msg,
                  lastUpdatedTimestamp: new Date().getTime()
                })
                return;
              } else if (ev.event === 'debug') {
                try {
                  const payload = JSON.parse(msg);
                  console.log('[Sales agent]', payload);
                } catch (e) {
                  console.log('[Sales agent debug]', msg);
                }
                return;
              } else if (ev.event === 'source_url') {
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  sourceUrl: msg,
                  lastUpdatedTimestamp: new Date().getTime()
                })
                return;
              } else if (ev.event === 'error') {
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  error: true,
                  reply: msg,
                  lastUpdatedTimestamp: new Date().getTime()
                })
                return;
              } else if (ev.event === 'message') {
                chatMessage.push(msg);
                Object.assign(this.messages[placeholderChatMessageIndex], {
                  ...this.messages[placeholderChatMessageIndex],
                  reply: chatMessage.join(""),
                  lastUpdatedTimestamp: new Date().getTime()
                });
                if (this.autoScrollEnabled) {
                  this.$nextTick(() => {
                    this.scrollToEnd();
                  })
                }
              }
            }
          })
        } catch (e) {
          console.error(e)
        } finally {
          ctrl.abort();
        }
        Object.assign(this.messages[placeholderChatMessageIndex], {
          ...this.messages[placeholderChatMessageIndex],
          lastUpdatedTimestamp: new Date().getTime(),
          inflight: false
        });
        this.scrollToEnd();
      } catch (error) {
        console.log(error);
        const messageIndex = this.messages.findIndex(m => m.id === uuid);
        if (messageIndex > -1) {
          placeholderChatMessage.error = true;
          Object.assign(this.messages[messageIndex], placeholderChatMessage);
        }
      }
      this.sendingMessage = false;
    },
    scrollToEnd() {
      this.$nextTick(() => {
        this.$vuetify.goTo('#chat-end', {duration: 0});
      })
    },
    async startNewChat() {
      const timestamp = new Date().getTime();
      const dummyChat = {
        id: `dummy-${timestamp}`,
        name: 'Uusi keskustelu',
        timestamp: timestamp,
      };

      this.chatId = dummyChat.id;
      this.activeChat = dummyChat.id;
      this.messages = [];
    },
    async createNewChat(messageText) {
      try {
        const chat = await salesApi.newChat({name: messageText, locale: getActiveRegionKey()});
        this.chatId = chat.id;
        this.activeChat = chat.id;
        chatStorage.value.chatId = this.chatId;
        await this.reloadMessages();
      } catch (error) {
        alert(error);
      }
    },
    setTemperature(value) {
      this.temperature = value;
    },
    async vote(message, vote) {
      const newRating = message.rating !== vote ? vote : 0;
      const previousRating = message.rating;
      const idx = this.messages.findIndex(m => m.id === message.originalId);
      if (idx > -1) {
        this.messages = [...this.messages];
        this.messages[idx] = { ...this.messages[idx], rating: newRating };
      }
      try {
        await salesApi.rating(message, newRating);
        if (newRating === -1) {
          this.$refs.supportTicketDialog.open(message);
        }
      } catch (e) {
        console.error(e);
        if (idx > -1) {
          this.messages = [...this.messages];
          this.messages[idx] = { ...this.messages[idx], rating: previousRating };
        }
      }
    },
  },
  async mounted() {
    axios.defaults.headers.common['x-api-key'] = store.state.sessionId;

    chatStorage = useSessionStorage('chat-store', {chatId: store.state.sessionId})

    this.startNewChat();
    this.loading = false;

    if (this.$root && this.$root.$on) {
      this.$root.$on('start-new-chat', this.startNewChat);
    }

    this.$nextTick(() => {
      const queryString = window.location.search;
      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        const searchQuery = urlParams.get('search_api_fulltext');
        if (searchQuery) {
          this.sendMessage(`${this.$t('searching_information_about_subject')}: ${searchQuery}`);
        }
      }
    });
  },
  beforeDestroy() {
    if (this.$root && this.$root.$off) {
      this.$root.$off('start-new-chat', this.startNewChat);
    }
  }
}
</script>

<style lang="scss">
.chat-input-holder {
  -webkit-box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
  -moz-box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
  box-shadow: 0px -2px 6px 7px rgba(204, 204, 204, 1);
}

.new-chat-button-holder {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

.user-info-holder {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
}

#chat-end {
  height: 1px;
  width: 100%;
}
</style>
